import {find} from 'lodash';
import {batch} from 'react-redux';
import {CopyPipettingValueMode} from 'constants/cardStateEnum';
import {CardType, Feedback, TaskType} from '../../constants/dataEnum';
import {hideLoading, showLoading, showSnackbar} from '../actions/appStateAction';
import {
  resetCopiedPipettingParameter,
  setAutosaveDisabled,
  setAutosaveEnabled,
  setCopiedPipettingParameter,
  setPendingSaveExistStatus,
} from '../actions/protocolEditorAction';
import {resolveIntl} from './commonBusiness';
// eslint-disable-next-line import/no-cycle
import {editGeneralTask} from './taskListBusinessAction';
import {batchAddCardTask, setCardIsExpanded} from '../actions/cardStateAction';
import {globalAddUndoItem} from './undoRedoBusinessAction';
import {saveState} from './stateBusiness';
import {clearTemporaryUnsavedProtocolData, setOpenedProtocolCheckpoint} from '../actions/protocolAction';
import {setProtocolCheckpoint} from './commonProtocolManagementBusiness';
import {saveProtocol} from './protocolManagementBusiness';
import {setAutoSaveProtocol} from './userManagementBusiness';
import {
  extractPipettingParameterFromTask,
  getAppliedPipettingParameters,
  showApplyPipettingConfirmationModal,
} from './pipettingParameterBusiness';

export const copyPipettingParameterTask = (task) => (dispatch) => {
  if (!task.type) return;

  const {type, labwareID} = task;
  const pipettingParameters = extractPipettingParameterFromTask(task);
  const copiedData = {type, labwareID, pipettingParameters};

  batch(() => {
    dispatch(resetCopiedPipettingParameter());
    dispatch(setCopiedPipettingParameter(copiedData));
  });
};

export const pastePipettingParameterTask = (task) => (dispatch, getState) => {
  if (!task.type) return {};
  const {protocolEditorState, labwareState, configState} = getState();
  const {copiedPipettingParameter} = protocolEditorState;

  const targetTask = task.taskList[0];
  const {deepWellThreshold} = configState;
  const intl = resolveIntl();

  const copiedTaskLabware = find(labwareState, {LabwareID: copiedPipettingParameter.labwareID});
  const targetTaskLabware = find(labwareState, {LabwareID: targetTask.labwareID});

  const isCopiedTaskLabwareDeepWell = copiedTaskLabware.wellDepth > deepWellThreshold;
  const isTargetTaskLabwareDeepWell = targetTaskLabware.wellDepth > deepWellThreshold;

  if (isCopiedTaskLabwareDeepWell === isTargetTaskLabwareDeepWell) {
    const sourceTask = {pipettingParameters: copiedPipettingParameter.pipettingParameters, labware: copiedTaskLabware};
    const destTask = {pipettingParameters: targetTask, labware: targetTaskLabware};

    const adjustedTaskData = dispatch(getAppliedPipettingParameters(sourceTask, destTask));
    dispatch(editGeneralTask(task.cardID, adjustedTaskData));
  } else {
    dispatch(
      showSnackbar(
        Feedback.ERROR,
        intl.formatMessage({
          id: 'protocolEditor.taskList.menuContext.notification.error.deepWellNotInterchangable',
        }),
      ),
    );
  }
  return {};
};

export const copyPipettingParameterCard = (tasksInCard) => (dispatch) => {
  dispatch(resetCopiedPipettingParameter());

  const aspDispPipettingParam = [];

  tasksInCard.forEach((task) => {
    if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
      const {type, labwareID} = task;
      const pipettingParameters = extractPipettingParameterFromTask(task);
      const taskForClipboard = {type, labwareID, pipettingParameters};

      aspDispPipettingParam.push(taskForClipboard);
    }
  });

  dispatch(
    setCopiedPipettingParameter({
      type: CardType.SIMPLE_TRANSFER,
      pipettingParameters: aspDispPipettingParam,
    }),
  );
};

export const showApplyPipettingConfirmationAndPasteToCard = (task) => async (dispatch) => {
  const callback = () => dispatch(pastePipettingParameterCard(task));

  await dispatch(showApplyPipettingConfirmationModal(CopyPipettingValueMode.TO_SINGLE_CARD, callback));
};

export const pastePipettingParameterCard = (tasksInTargetCards) => (dispatch, getState) => {
  const {cardID, cardName, tasks} = tasksInTargetCards;
  const {protocolEditorState, labwareState, configState} = getState();
  const {copiedPipettingParameter} = protocolEditorState;
  const {pipettingParameters} = copiedPipettingParameter;
  const {deepWellThreshold} = configState;

  const intl = resolveIntl();
  const newCardTask = [];
  let idxCopiedData = 0;
  let error;
  const labwareDataById = {};

  Object.values(labwareState).forEach((labware) => {
    if (labware.LabwareID) {
      labwareDataById[labware.LabwareID] = labware;
    }
  });

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i];
    let newParameters = {};

    if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
      const copiedParam = pipettingParameters[idxCopiedData];

      if (task.type !== copiedParam?.type) {
        error = 'wrongTaskSequence';
        break;
      }

      const copiedTaskLabware = labwareDataById[copiedParam.labwareID];
      const targetTaskLabware = labwareDataById[task.labwareID];

      const isCopiedTaskLabwareDeepWell = copiedTaskLabware.wellDepth > deepWellThreshold;
      const isTargetTaskLabwareDeepWell = targetTaskLabware.wellDepth > deepWellThreshold;

      if (task.type === copiedParam?.type && isCopiedTaskLabwareDeepWell === isTargetTaskLabwareDeepWell) {
        const sourceTask = {pipettingParameters: copiedParam.pipettingParameters, labware: copiedTaskLabware};
        const destTask = {pipettingParameters: task, labware: targetTaskLabware};

        newParameters = dispatch(getAppliedPipettingParameters(sourceTask, destTask));
        idxCopiedData += 1;
      } else if (isCopiedTaskLabwareDeepWell !== isTargetTaskLabwareDeepWell) {
        error = 'deepWellNotInterchangable';
        break;
      }
    }

    newCardTask.push({...task, ...newParameters});
  }

  if (!error) {
    if (idxCopiedData === pipettingParameters.length) {
      dispatch(batchAddCardTask(cardID, cardName, CardType.SIMPLE_TRANSFER, newCardTask));
      dispatch(setCardIsExpanded(cardID, true));
      dispatch(globalAddUndoItem());
      return;
    }

    // For a case when the asp/disp tasks in the targeted card is fewer than the copied card's
    // but has the same sequence so far so the error condition wasn't triggered
    error = 'wrongTaskSequence';
  }

  dispatch(
    showSnackbar(
      Feedback.ERROR,
      intl.formatMessage({
        id: `protocolEditor.taskList.menuContext.notification.error.${error}`,
      }),
    ),
  );
};

export const enableAutosave = () => async (dispatch) => {
  dispatch(showLoading());

  await dispatch(saveProtocol(true));
  await dispatch(setAutoSaveProtocol(true));

  dispatch(setAutosaveEnabled());
  dispatch(setOpenedProtocolCheckpoint(null));
  dispatch(clearTemporaryUnsavedProtocolData());

  await dispatch(saveState());
  dispatch(hideLoading());
};

export const disableAutosave = () => async (dispatch) => {
  dispatch(setAutosaveDisabled());
  await dispatch(setAutoSaveProtocol(false));
  await dispatch(setProtocolCheckpoint());
};

export const resetProtocolPendingSaveStates = () => (dispatch) => {
  batch(() => {
    dispatch(setPendingSaveExistStatus(false));
    dispatch(setOpenedProtocolCheckpoint(null));
    dispatch(clearTemporaryUnsavedProtocolData());
  });
};
